// src/constants/macro/fxCommoditiesColumns.tsx
/**
 * Column definitions for FX & Commodities tables.
 * Uses the reusable column builders for consistent styling.
 */

import { currencyColumn, percentColumn } from "@/components/tables";
import type { SortableColumn } from "@/components/tables";
import type { FxCommodity } from "@/types/dashboards/macro";
import { getCurrencyFlag } from "@/utils/countryFlags";

/**
 * Asset name column with category subtitle and country flag
 */
const assetNameColumn: SortableColumn<FxCommodity> = {
  id: "asset_name",
  header: "Asset",
  align: "left",
  cell: (row: FxCommodity) => {
    const flag = getCurrencyFlag(row.asset_name);
    return (
      <div className="flex items-center gap-2 min-w-0">
        {flag && <span className="text-base flex-shrink-0">{flag}</span>}
        <div className="flex flex-col min-w-0">
          <span className="text-slate-100 font-medium text-sm truncate">
            {row.asset_name}
          </span>
          {row.category && (
            <span className="text-slate-400 text-xs">{row.category}</span>
          )}
        </div>
      </div>
    );
  },
  sortValue: (row: FxCommodity) => row.asset_name,
};

/**
 * Day change column (absolute value)
 */
const dayChangeColumn: SortableColumn<FxCommodity> = {
  id: "day_change",
  header: "Day Chg",
  align: "right",
  cell: (row: FxCommodity) => {
    const value = row.day_change;
    if (value == null || !Number.isFinite(value)) {
      return <span className="text-slate-400">–</span>;
    }
    const sign = value >= 0 ? "+" : "";
    const colorClass =
      value > 0
        ? "text-emerald-400"
        : value < 0
        ? "text-rose-400"
        : "text-slate-400";
    return (
      <span className={`tabular-nums font-semibold ${colorClass}`}>
        {sign}
        {value.toFixed(4)}
      </span>
    );
  },
  sortValue: (row: FxCommodity) => row.day_change ?? -Infinity,
};

/**
 * Date column
 */
const dateColumn: SortableColumn<FxCommodity> = {
  id: "unit_date",
  header: "Date",
  align: "right",
  cell: (row: FxCommodity) => (
    <span className="text-slate-400 text-xs">{row.unit_date ?? "–"}</span>
  ),
  sortValue: (row: FxCommodity) => row.unit_date ?? "",
};

/**
 * Complete column definitions for FX & Commodities table
 */
export const FX_COMMODITIES_COLUMNS: SortableColumn<FxCommodity>[] = [
  assetNameColumn,
  currencyColumn<FxCommodity>({
    id: "price",
    header: "Price",
    accessor: (r) => r.price,
    symbol: "",
    compact: false,
    decimals: 4,
  }),
  dayChangeColumn,
  percentColumn<FxCommodity>({
    id: "day_change_pct",
    header: "Day %",
    accessor: (r) => r.day_change_pct,
    alreadyPercent: true,
    decimals: 2,
  }),
  percentColumn<FxCommodity>({
    id: "weekly_change_pct",
    header: "Week %",
    accessor: (r) => r.weekly_change_pct,
    alreadyPercent: true,
    decimals: 2,
  }),
  percentColumn<FxCommodity>({
    id: "monthly_change_pct",
    header: "Month %",
    accessor: (r) => r.monthly_change_pct,
    alreadyPercent: true,
    decimals: 2,
  }),
  percentColumn<FxCommodity>({
    id: "ytd_change_pct",
    header: "YTD %",
    accessor: (r) => r.ytd_change_pct,
    alreadyPercent: true,
    decimals: 2,
  }),
  percentColumn<FxCommodity>({
    id: "yoy_change_pct",
    header: "YoY %",
    accessor: (r) => r.yoy_change_pct,
    alreadyPercent: true,
    decimals: 2,
  }),
  dateColumn,
];
